import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/restaurants/warehouses';
export const getWarehouseUnassignedSuppliersEndpointIdentifier = endpoint;

type TData = Record<string, any>;
type TError = AxiosError;
type TQueryKey = [
  string,
  {
    name: TParam;
  }
];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

function constructEndpoint(restaurantId: string, warehouseId: string) {
  return `${endpoint}/${restaurantId}/${warehouseId}/suppliers/unassigned`;
}

export function useGetWarehouseUnassignedSuppliersApi(
  restaurantId: string,
  warehouseId: string,
  options?: Options
): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { name } = getPageParams();

  const fullEndpoint = constructEndpoint(restaurantId, warehouseId);

  const queryFn = async () => {
    try {
      const { data } = await http.get(fullEndpoint, {
        params: { page: 1, limit: 100, name },
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([fullEndpoint, { name }], queryFn, {
    enabled: isLoggedIn,
    ...options,
  });
}
